import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import { toast } from "react-hot-toast";

import {
  UserContext,
} from "../userContext/userContext";

import {
  usePromotions,
} from "../promotionsContext/promotionsContext";

const PRODUCT_CART_STORAGE_KEY =
  "shoppingCart";

const BUNDLE_CART_STORAGE_KEY =
  "shoppingCartBundles";

export const ShoppingCartContext =
  createContext({
    cartItems: [],
    cartBundles: [],

    productSubtotal: 0,

    bundleBaseSubtotal: 0,
    bundlePromotedSubtotal: 0,
    bundleSubtotal: 0,

    bundlePromotionDiscount: 0,
    bundleOwnershipCredit: 0,
    bundleTotalSavings: 0,

    cartSubtotal: 0,
    cartItemCount: 0,
    cartPricingReady: true,
    loadingBundlePromotions: false,

    addToCart: async () => false,
    addBundleToCart: async () => false,

    removeFromCart: () => {},
    removeBundleFromCart: () => {},
    clearShoppingCart: () => {},

    isOwnedSku: () => false,
    isSkuInCart: () => false,
    isBundleInCart: () => false,
    isSkuIncludedInCartBundle:
      () => false,

    checkoutProductSkus: [],
    checkoutBundleSkus: [],

    checkoutPayload: {
      productSkus: [],
      bundleSkus: [],
    },
  });

function normalizeSku(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value).trim();
}

function roundMoney(value) {
  return (
    Math.round(
      (
        Number(value || 0) +
        Number.EPSILON
      ) *
        100
    ) / 100
  );
}

function loadSavedArray(
  storageKey
) {
  try {
    const savedValue =
      localStorage.getItem(
        storageKey
      );

    if (!savedValue) {
      return [];
    }

    const parsedValue =
      JSON.parse(savedValue);

    return Array.isArray(
      parsedValue
    )
      ? parsedValue
      : [];
  } catch (error) {
    console.error(
      `Unable to read ${storageKey}:`,
      error
    );

    localStorage.removeItem(
      storageKey
    );

    return [];
  }
}

function getUniqueSkus(values) {
  if (!Array.isArray(values)) {
    return [];
  }

  return [
    ...new Set(
      values
        .map((value) => {
          if (
            value &&
            typeof value ===
              "object"
          ) {
            return normalizeSku(
              value.SKU ||
                value.sku
            );
          }

          return normalizeSku(
            value
          );
        })
        .filter(Boolean)
    ),
  ];
}

function getOwnedSkuSet(
  licenses = []
) {
  const ownedSkus =
    new Set();

  if (
    !Array.isArray(licenses)
  ) {
    return ownedSkus;
  }

  for (
    const license of licenses
  ) {
    const licenseType =
      String(
        license?.type || ""
      )
        .trim()
        .toLowerCase();

    /*
     * Subscription licenses do not
     * count as permanent ownership.
     */
    if (
      licenseType !== "own"
    ) {
      continue;
    }

    const sku =
      normalizeSku(
        license?.sku
      );

    if (sku) {
      ownedSkus.add(sku);
    }
  }

  return ownedSkus;
}

/*
 * Calculates the amount still due
 * for a bundle after:
 *
 * 1. Applying the best active
 *    promotions to each included
 *    product.
 *
 * 2. Reducing that product's
 *    proportional share of the
 *    bundle price by the same
 *    percentage.
 *
 * 3. Removing the promoted bundle
 *    share of products the customer
 *    permanently owns.
 *
 * Example:
 *
 * Product A: $60
 * Product B: $40
 * Bundle price: $50
 *
 * Before promotions:
 *
 * A bundle share: $30
 * B bundle share: $20
 *
 * If Product A is 50% off:
 *
 * A promoted bundle share: $15
 * B bundle share: $20
 *
 * Promotion-adjusted bundle price:
 * $35
 *
 * If the customer already owns
 * Product B, the final bundle price
 * becomes $15.
 */
function calculateBundleCartPricing(
  bundle,
  licenses = [],
  getCartPricing = null
) {
  const ownedSkuSet =
    getOwnedSkuSet(licenses);

  const bundleProductSkus =
    getUniqueSkus(
      bundle?.products
    );

  const rawIncludedProducts =
    Array.isArray(
      bundle?.includedProducts
    )
      ? bundle.includedProducts
      : [];

  const productMap =
    new Map();

  for (
    const product of
    rawIncludedProducts
  ) {
    const sku =
      normalizeSku(
        product?.SKU
      );

    if (sku) {
      productMap.set(
        sku,
        product
      );
    }
  }

  /*
   * Preserve the order configured
   * on the bundle.
   */
  const orderedProducts =
    bundleProductSkus
      .map((sku) =>
        productMap.get(sku)
      )
      .filter(Boolean);

  /*
   * Permanently owned products
   * should not:
   *
   * - Help qualify for a BOGO offer.
   * - Influence which promotion is
   *   selected.
   * - Receive a promotion discount.
   *
   * They are removed separately
   * through ownership credit.
   */
  const promotionEligibleProducts =
    orderedProducts.filter(
      (product) =>
        !ownedSkuSet.has(
          normalizeSku(
            product?.SKU
          )
        )
    );

  let promotionPricing = {
    lineItems: [],

    originalSubtotal: 0,

    promotionsDiscountTotal:
      0,

    finalSubtotal: 0,

    appliedPromotions: [],

    availablePromotions: [],

    selectedPromotionIds: [],
  };

  if (
    promotionEligibleProducts
      .length > 0 &&
    typeof getCartPricing ===
      "function"
  ) {
    try {
      promotionPricing =
        getCartPricing(
          promotionEligibleProducts.map(
            (product) => ({
              ...product,
              quantity: 1,
            })
          ),
          {
            useAutomaticBestDeal:
              true,

            selectedPromotionIds:
              [],
          }
        ) ||
        promotionPricing;
    } catch (error) {
      /*
       * Do not make the cart unusable
       * if promotion calculation
       * fails. Fall back to normal
       * product prices.
       */
      console.error(
        "Unable to calculate bundle promotions:",
        error
      );
    }
  }

  const promotionLineMap =
    new Map();

  const promotionLines =
    Array.isArray(
      promotionPricing
        ?.lineItems
    )
      ? promotionPricing.lineItems
      : [];

  for (
    const line of
    promotionLines
  ) {
    const sku =
      normalizeSku(
        line?.item?.SKU
      );

    if (sku) {
      promotionLineMap.set(
        sku,
        line
      );
    }
  }

  const ownedProductSkus =
    [];

  const purchasableProductSkus =
    [];

  const ownedProducts = [];

  const purchasableProducts =
    [];

  const includedProducts = [];

  const promotionPricingBySku =
    {};

  let includedProductTotal =
    0;

  let promotedIncludedProductTotal =
    0;

  let ownedProductTotal = 0;

  let promotedOwnedProductTotal =
    0;

  let purchasableProductTotal =
    0;

  let promotedPurchasableProductTotal =
    0;

  for (
    const sku of
    bundleProductSkus
  ) {
    const product =
      productMap.get(sku);

    const originalProductPrice =
      roundMoney(
        Math.max(
          0,
          Number(
            product?.price || 0
          )
        )
      );

    const productIsOwned =
      ownedSkuSet.has(sku);

    const promotionLine =
      promotionLineMap.get(
        sku
      );

    /*
     * Owned products remain at their
     * normal product price during the
     * promotion stage. Their share is
     * removed afterward as ownership
     * credit.
     */
    const promotedProductPrice =
      productIsOwned
        ? originalProductPrice
        : roundMoney(
            Math.max(
              0,
              Math.min(
                originalProductPrice,

                Number.isFinite(
                  Number(
                    promotionLine
                      ?.discountedLineTotal
                  )
                )
                  ? Number(
                      promotionLine
                        .discountedLineTotal
                    )
                  : originalProductPrice
              )
            )
          );

    const productPromotionDiscount =
      roundMoney(
        Math.max(
          0,

          originalProductPrice -
            promotedProductPrice
        )
      );

    const productPromotionPricing =
      {
        originalPrice:
          originalProductPrice,

        finalPrice:
          promotedProductPrice,

        discountAmount:
          productPromotionDiscount,

        hasDiscount:
          productPromotionDiscount >
          0,

        appliedPromotions:
          Array.isArray(
            promotionLine
              ?.appliedPromotions
          )
            ? promotionLine
                .appliedPromotions
            : [],

        promotionLabels:
          Array.isArray(
            promotionLine
              ?.promotionLabels
          )
            ? promotionLine
                .promotionLabels
            : [],

        promotionLabel:
          promotionLine
            ?.promotionLabel ||
          promotionLine
            ?.promotionLabels?.[0] ||
          "",
      };

    promotionPricingBySku[
      sku
    ] =
      productPromotionPricing;

    const productWithPricing =
      product
        ? {
            ...product,

            cartPromotionPricing:
              productPromotionPricing,
          }
        : {
            SKU: sku,

            price:
              originalProductPrice,

            cartPromotionPricing:
              productPromotionPricing,
          };

    includedProducts.push(
      productWithPricing
    );

    includedProductTotal +=
      originalProductPrice;

    promotedIncludedProductTotal +=
      promotedProductPrice;

    if (productIsOwned) {
      ownedProductSkus.push(
        sku
      );

      ownedProductTotal +=
        originalProductPrice;

      promotedOwnedProductTotal +=
        promotedProductPrice;

      ownedProducts.push(
        productWithPricing
      );
    } else {
      purchasableProductSkus.push(
        sku
      );

      purchasableProductTotal +=
        originalProductPrice;

      promotedPurchasableProductTotal +=
        promotedProductPrice;

      purchasableProducts.push(
        productWithPricing
      );
    }
  }

  includedProductTotal =
    roundMoney(
      includedProductTotal
    );

  promotedIncludedProductTotal =
    roundMoney(
      promotedIncludedProductTotal
    );

  ownedProductTotal =
    roundMoney(
      ownedProductTotal
    );

  promotedOwnedProductTotal =
    roundMoney(
      promotedOwnedProductTotal
    );

  purchasableProductTotal =
    roundMoney(
      purchasableProductTotal
    );

  promotedPurchasableProductTotal =
    roundMoney(
      promotedPurchasableProductTotal
    );

  const baseBundlePrice =
    roundMoney(
      Math.max(
        0,
        Number(
          bundle?.price || 0
        )
      )
    );

  let promotedBundlePrice =
    baseBundlePrice;

  let adjustedPrice =
    baseBundlePrice;

  if (
    bundleProductSkus.length ===
    0
  ) {
    promotedBundlePrice = 0;
    adjustedPrice = 0;
  } else if (
    includedProductTotal > 0
  ) {
    /*
     * Promotion-adjusted bundle
     * price before ownership credit.
     *
     * Owned products are still
     * included at their normal
     * weight here.
     */
    promotedBundlePrice =
      roundMoney(
        baseBundlePrice *
          (
            promotedIncludedProductTotal /
            includedProductTotal
          )
      );

    /*
     * Final bundle price includes
     * only the promoted value of
     * unowned products.
     */
    adjustedPrice =
      roundMoney(
        baseBundlePrice *
          (
            promotedPurchasableProductTotal /
            includedProductTotal
          )
      );
  } else {
    /*
     * Fallback for a bundle that
     * contains only zero-priced
     * products.
     */
    promotedBundlePrice =
      baseBundlePrice;

    adjustedPrice =
      purchasableProductSkus
        .length === 0
        ? 0
        : roundMoney(
            baseBundlePrice *
              (
                purchasableProductSkus
                  .length /
                bundleProductSkus
                  .length
              )
          );
  }

  promotedBundlePrice =
    roundMoney(
      Math.max(
        0,
        Math.min(
          baseBundlePrice,
          promotedBundlePrice
        )
      )
    );

  adjustedPrice =
    roundMoney(
      Math.max(
        0,
        Math.min(
          promotedBundlePrice,
          adjustedPrice
        )
      )
    );

  /*
   * Keep promotion savings and
   * ownership credit separate so
   * they can be labeled accurately.
   */
  const bundlePromotionDiscount =
    roundMoney(
      Math.max(
        0,

        baseBundlePrice -
          promotedBundlePrice
      )
    );

  const ownershipCredit =
    roundMoney(
      Math.max(
        0,

        promotedBundlePrice -
          adjustedPrice
      )
    );

  const totalSavings =
    roundMoney(
      Math.max(
        0,

        baseBundlePrice -
          adjustedPrice
      )
    );

  const appliedPromotions =
    Array.isArray(
      promotionPricing
        ?.appliedPromotions
    )
      ? promotionPricing
          .appliedPromotions
      : [];

  const promotionLabels = [
    ...new Set(
      Object.values(
        promotionPricingBySku
      ).flatMap(
        (pricing) =>
          pricing
            .promotionLabels ||
          []
      )
    ),
  ];

  return {
    baseBundlePrice,
    promotedBundlePrice,
    adjustedPrice,

    bundlePromotionDiscount,
    ownershipCredit,
    totalSavings,

    includedProductTotal,
    promotedIncludedProductTotal,

    ownedProductTotal,
    promotedOwnedProductTotal,

    purchasableProductTotal,
    promotedPurchasableProductTotal,

    ownedProductSkus,
    purchasableProductSkus,

    includedProducts,
    ownedProducts,
    purchasableProducts,

    promotionPricingBySku,
    appliedPromotions,
    promotionLabels,

    hasPromotionDiscount:
      bundlePromotionDiscount >
      0,

    hasOwnedProducts:
      ownedProductSkus.length >
      0,

    partiallyOwned:
      ownedProductSkus.length >
        0 &&
      purchasableProductSkus
        .length > 0,

    fullyOwned:
      bundleProductSkus.length >
        0 &&
      purchasableProductSkus
        .length === 0,
  };
}

export const ShoppingCartProvider =
  ({
    children,
  }) => {
    const {
      user,

      userLicenses = [],

      licensesLoaded = false,

      refreshLicenses,
    } = useContext(
      UserContext
    );

    const {
      getCartPricing,

      loadingPromotions,
    } = usePromotions();

    /*
     * Individual product line
     * items.
     */
    const [
      cartItems,
      setCartItems,
    ] = useState(() =>
      loadSavedArray(
        PRODUCT_CART_STORAGE_KEY
      )
    );

    /*
     * Bundle line items remain
     * separate from individual
     * products.
     */
    const [
      storedCartBundles,
      setStoredCartBundles,
    ] = useState(() =>
      loadSavedArray(
        BUNDLE_CART_STORAGE_KEY
      )
    );

    const hasAuthentication =
      Boolean(
        user ||
          localStorage.getItem(
            "token"
          )
      );

    /*
     * Bundle pricing is ready when:
     *
     * - Licenses are loaded for an
     *   authenticated customer.
     * - Active promotions have
     *   finished loading.
     */
    const cartPricingReady =
      (
        !hasAuthentication ||
        licensesLoaded
      ) &&
      !loadingPromotions;

    /*
     * Add calculated ownership and
     * promotion details to every
     * stored bundle.
     */
    const cartBundles =
      useMemo(
        () =>
          storedCartBundles.map(
            (bundle) => {
              const pricing =
                calculateBundleCartPricing(
                  bundle,

                  licensesLoaded
                    ? userLicenses
                    : [],

                  getCartPricing
                );

              return {
                ...bundle,

                /*
                 * Use the enriched
                 * products so the
                 * cart can display
                 * each product's
                 * promotion.
                 */
                includedProducts:
                  pricing
                    .includedProducts,

                cartType:
                  "bundle",

                /*
                 * Keep both names
                 * for compatibility
                 * with existing
                 * pages.
                 */
                basePrice:
                  pricing
                    .baseBundlePrice,

                baseBundlePrice:
                  pricing
                    .baseBundlePrice,

                promotedBundlePrice:
                  pricing
                    .promotedBundlePrice,

                adjustedPrice:
                  pricing
                    .adjustedPrice,

                bundlePromotionDiscount:
                  pricing
                    .bundlePromotionDiscount,

                promotionDiscount:
                  pricing
                    .bundlePromotionDiscount,

                ownershipCredit:
                  pricing
                    .ownershipCredit,

                totalSavings:
                  pricing
                    .totalSavings,

                includedProductTotal:
                  pricing
                    .includedProductTotal,

                promotedIncludedProductTotal:
                  pricing
                    .promotedIncludedProductTotal,

                ownedProductTotal:
                  pricing
                    .ownedProductTotal,

                promotedOwnedProductTotal:
                  pricing
                    .promotedOwnedProductTotal,

                purchasableProductTotal:
                  pricing
                    .purchasableProductTotal,

                promotedPurchasableProductTotal:
                  pricing
                    .promotedPurchasableProductTotal,

                ownedProductSkus:
                  pricing
                    .ownedProductSkus,

                purchasableProductSkus:
                  pricing
                    .purchasableProductSkus,

                ownedProducts:
                  pricing
                    .ownedProducts,

                purchasableProducts:
                  pricing
                    .purchasableProducts,

                promotionPricingBySku:
                  pricing
                    .promotionPricingBySku,

                appliedPromotions:
                  pricing
                    .appliedPromotions,

                promotionLabels:
                  pricing
                    .promotionLabels,

                hasPromotionDiscount:
                  pricing
                    .hasPromotionDiscount,

                hasOwnedProducts:
                  pricing
                    .hasOwnedProducts,

                partiallyOwned:
                  pricing
                    .partiallyOwned,

                fullyOwned:
                  pricing
                    .fullyOwned,
              };
            }
          ),
        [
          storedCartBundles,
          licensesLoaded,
          userLicenses,
          getCartPricing,
        ]
      );

    const productSubtotal =
      useMemo(() => {
        return roundMoney(
          cartItems.reduce(
            (
              subtotal,
              item
            ) =>
              subtotal +
              Math.max(
                0,
                Number(
                  item?.price ||
                    0
                )
              ),
            0
          )
        );
      }, [cartItems]);

    const bundleBaseSubtotal =
      useMemo(() => {
        return roundMoney(
          cartBundles.reduce(
            (
              subtotal,
              bundle
            ) =>
              subtotal +
              Math.max(
                0,
                Number(
                  bundle
                    .baseBundlePrice ||
                    0
                )
              ),
            0
          )
        );
      }, [cartBundles]);

    const bundlePromotedSubtotal =
      useMemo(() => {
        return roundMoney(
          cartBundles.reduce(
            (
              subtotal,
              bundle
            ) =>
              subtotal +
              Math.max(
                0,
                Number(
                  bundle
                    .promotedBundlePrice ||
                    0
                )
              ),
            0
          )
        );
      }, [cartBundles]);

    const bundleSubtotal =
      useMemo(() => {
        return roundMoney(
          cartBundles.reduce(
            (
              subtotal,
              bundle
            ) =>
              subtotal +
              Math.max(
                0,
                Number(
                  bundle
                    .adjustedPrice ||
                    0
                )
              ),
            0
          )
        );
      }, [cartBundles]);

    const bundlePromotionDiscount =
      useMemo(() => {
        return roundMoney(
          cartBundles.reduce(
            (
              total,
              bundle
            ) =>
              total +
              Math.max(
                0,
                Number(
                  bundle
                    .bundlePromotionDiscount ||
                    0
                )
              ),
            0
          )
        );
      }, [cartBundles]);

    const bundleOwnershipCredit =
      useMemo(() => {
        return roundMoney(
          cartBundles.reduce(
            (
              total,
              bundle
            ) =>
              total +
              Math.max(
                0,
                Number(
                  bundle
                    .ownershipCredit ||
                    0
                )
              ),
            0
          )
        );
      }, [cartBundles]);

    const bundleTotalSavings =
      useMemo(() => {
        return roundMoney(
          bundlePromotionDiscount +
            bundleOwnershipCredit
        );
      }, [
        bundlePromotionDiscount,
        bundleOwnershipCredit,
      ]);

    /*
     * This subtotal does not apply
     * individual-product promotions.
     *
     * Your Shopping Cart and Checkout
     * pages already use getCartPricing
     * for individual product lines.
     */
    const cartSubtotal =
      useMemo(
        () =>
          roundMoney(
            productSubtotal +
              bundleSubtotal
          ),
        [
          productSubtotal,
          bundleSubtotal,
        ]
      );

    const cartItemCount =
      cartItems.length +
      cartBundles.length;

    /*
     * Persist products and bundles
     * separately.
     */
    useEffect(() => {
      localStorage.setItem(
        PRODUCT_CART_STORAGE_KEY,

        JSON.stringify(
          cartItems
        )
      );
    }, [cartItems]);

    useEffect(() => {
      localStorage.setItem(
        BUNDLE_CART_STORAGE_KEY,

        JSON.stringify(
          storedCartBundles
        )
      );
    }, [
      storedCartBundles,
    ]);

    const isOwnedSku =
      useCallback(
        (
          productSKU,

          licenses =
            userLicenses
        ) => {
          const sku =
            normalizeSku(
              productSKU
            );

          if (!sku) {
            return false;
          }

          return getOwnedSkuSet(
            licenses
          ).has(sku);
        },
        [userLicenses]
      );

    const isSkuInCart =
      useCallback(
        (productSKU) => {
          const sku =
            normalizeSku(
              productSKU
            );

          return cartItems.some(
            (item) =>
              normalizeSku(
                item?.SKU
              ) === sku
          );
        },
        [cartItems]
      );

    const isBundleInCart =
      useCallback(
        (bundleSKU) => {
          const sku =
            normalizeSku(
              bundleSKU
            );

          return storedCartBundles.some(
            (bundle) =>
              normalizeSku(
                bundle?.SKU
              ) === sku
          );
        },
        [storedCartBundles]
      );

    const isSkuIncludedInCartBundle =
      useCallback(
        (productSKU) => {
          const sku =
            normalizeSku(
              productSKU
            );

          if (!sku) {
            return false;
          }

          return cartBundles.some(
            (bundle) =>
              bundle
                .purchasableProductSkus
                .some(
                  (
                    includedSku
                  ) =>
                    normalizeSku(
                      includedSku
                    ) === sku
                )
          );
        },
        [cartBundles]
      );

    /*
     * Retrieve the latest license
     * information before making a
     * purchase decision.
     */
    const getVerifiedLicenses =
      useCallback(async () => {
        let currentLicenses =
          Array.isArray(
            userLicenses
          )
            ? userLicenses
            : [];

        const token =
          localStorage.getItem(
            "token"
          );

        if (
          token &&
          !licensesLoaded
        ) {
          if (
            typeof refreshLicenses !==
            "function"
          ) {
            throw new Error(
              "License verification is unavailable."
            );
          }

          const refreshedLicenses =
            await refreshLicenses(
              token
            );

          currentLicenses =
            Array.isArray(
              refreshedLicenses
            )
              ? refreshedLicenses
              : [];
        }

        return currentLicenses;
      }, [
        userLicenses,
        licensesLoaded,
        refreshLicenses,
      ]);

    const fetchProductInfo =
      useCallback(
        async (
          productSKU
        ) => {
          const sku =
            normalizeSku(
              productSKU
            );

          const {
            data,
          } = await axios.post(
            "/api/get_product",
            {
              productSKU:
                sku,
            }
          );

          if (
            !data ||
            data.error
          ) {
            throw new Error(
              data?.error ||
                `Product ${sku} could not be found.`
            );
          }

          if (
            normalizeSku(
              data.SKU
            ) !== sku
          ) {
            throw new Error(
              `Product ${sku} could not be verified.`
            );
          }

          return data;
        },
        []
      );

    const fetchBundleInfo =
      useCallback(
        async (
          bundleSKU
        ) => {
          const sku =
            normalizeSku(
              bundleSKU
            );

          const {
            data,
          } = await axios.post(
            "/api/get-bundle",
            {
              SKU: sku,
            }
          );

          if (
            !data ||
            data.error
          ) {
            throw new Error(
              data?.error ||
                `Bundle ${sku} could not be found.`
            );
          }

          const bundle =
            data.bundle ||
            data;

          if (
            normalizeSku(
              bundle?.SKU
            ) !== sku
          ) {
            throw new Error(
              `Bundle ${sku} could not be verified.`
            );
          }

          return {
            bundle,

            includedProducts:
              Array.isArray(
                data
                  .includedProducts
              )
                ? data
                    .includedProducts
                : [],
          };
        },
        []
      );

    const fetchBundleProducts =
      useCallback(
        async (
          bundle,
          providedProducts = []
        ) => {
          const productSkus =
            getUniqueSkus(
              bundle?.products
            );

          if (
            productSkus.length ===
            0
          ) {
            throw new Error(
              "This bundle does not contain any products."
            );
          }

          /*
           * Use product data returned
           * by get-bundle when all
           * products are present.
           */
          const providedProductMap =
            new Map(
              (
                Array.isArray(
                  providedProducts
                )
                  ? providedProducts
                  : []
              )
                .filter(
                  (product) =>
                    normalizeSku(
                      product?.SKU
                    )
                )
                .map(
                  (product) => [
                    normalizeSku(
                      product.SKU
                    ),

                    product,
                  ]
                )
            );

          const missingSkus =
            productSkus.filter(
              (sku) =>
                !providedProductMap.has(
                  sku
                )
            );

          if (
            missingSkus.length > 0
          ) {
            const fetchedProducts =
              await Promise.all(
                missingSkus.map(
                  (sku) =>
                    fetchProductInfo(
                      sku
                    )
                )
              );

            for (
              const product of
              fetchedProducts
            ) {
              const sku =
                normalizeSku(
                  product?.SKU
                );

              if (sku) {
                providedProductMap.set(
                  sku,
                  product
                );
              }
            }
          }

          return productSkus
            .map((sku) =>
              providedProductMap.get(
                sku
              )
            )
            .filter(Boolean);
        },
        [fetchProductInfo]
      );

    /*
     * Remove permanently owned
     * individual products once
     * licenses finish loading.
     *
     * Also remove a bundle when the
     * customer owns every included
     * product.
     */
    useEffect(() => {
      if (
        !licensesLoaded ||
        !Array.isArray(
          userLicenses
        )
      ) {
        return;
      }

      const ownedSkuSet =
        getOwnedSkuSet(
          userLicenses
        );

      setCartItems(
        (previousItems) => {
          const filteredItems =
            previousItems.filter(
              (item) =>
                !ownedSkuSet.has(
                  normalizeSku(
                    item?.SKU
                  )
                )
            );

          return filteredItems
            .length ===
            previousItems.length
            ? previousItems
            : filteredItems;
        }
      );

      setStoredCartBundles(
        (
          previousBundles
        ) => {
          const filteredBundles =
            previousBundles.filter(
              (bundle) => {
                const pricing =
                  calculateBundleCartPricing(
                    bundle,

                    userLicenses,

                    getCartPricing
                  );

                return !pricing
                  .fullyOwned;
              }
            );

          return filteredBundles
            .length ===
            previousBundles.length
            ? previousBundles
            : filteredBundles;
        }
      );
    }, [
      licensesLoaded,
      userLicenses,
      getCartPricing,
    ]);

    /*
     * An individual product should
     * not remain in the cart when it
     * is already being purchased
     * through a bundle.
     */
    useEffect(() => {
      if (
        cartBundles.length ===
          0 ||
        cartItems.length === 0
      ) {
        return;
      }

      const bundledSkuSet =
        new Set(
          cartBundles.flatMap(
            (bundle) =>
              bundle
                .purchasableProductSkus
                .map(
                  normalizeSku
                )
          )
        );

      setCartItems(
        (previousItems) => {
          const filteredItems =
            previousItems.filter(
              (item) =>
                !bundledSkuSet.has(
                  normalizeSku(
                    item?.SKU
                  )
                )
            );

          return filteredItems
            .length ===
            previousItems.length
            ? previousItems
            : filteredItems;
        }
      );
    }, [
      cartBundles,
      cartItems.length,
    ]);

    const addToCart =
      useCallback(
        async (
          productSKU
        ) => {
          const sku =
            normalizeSku(
              productSKU
            );

          if (!sku) {
            toast.error(
              "A product SKU is required."
            );

            return false;
          }

          let currentLicenses;

          try {
            currentLicenses =
              await getVerifiedLicenses();
          } catch (error) {
            console.error(
              "Unable to verify product ownership:",
              error
            );

            toast.error(
              "We could not verify your licenses. Please try again."
            );

            return false;
          }

          if (
            isOwnedSku(
              sku,
              currentLicenses
            )
          ) {
            toast.error(
              "You have already purchased this product."
            );

            setCartItems(
              (
                previousItems
              ) =>
                previousItems.filter(
                  (item) =>
                    normalizeSku(
                      item?.SKU
                    ) !== sku
                )
            );

            return false;
          }

          /*
           * Prevent purchasing the
           * same product both
           * individually and through
           * a bundle.
           */
          const includedInBundle =
            storedCartBundles.some(
              (bundle) => {
                const pricing =
                  calculateBundleCartPricing(
                    bundle,

                    currentLicenses,

                    getCartPricing
                  );

                return pricing
                  .purchasableProductSkus
                  .some(
                    (
                      includedSku
                    ) =>
                      normalizeSku(
                        includedSku
                      ) === sku
                  );
              }
            );

          if (
            includedInBundle
          ) {
            toast(
              "This product is already included in a bundle in your cart."
            );

            return false;
          }

          if (
            isSkuInCart(sku)
          ) {
            toast(
              "This product is already in your shopping cart."
            );

            return false;
          }

          let productInfo;

          try {
            productInfo =
              await fetchProductInfo(
                sku
              );
          } catch (error) {
            console.error(
              "Unable to retrieve product information:",
              error
            );

            toast.error(
              error?.response
                ?.data?.error ||
                error?.message ||
                "Failed to retrieve product details."
            );

            return false;
          }

          setCartItems(
            (
              previousItems
            ) => {
              const alreadyExists =
                previousItems.some(
                  (item) =>
                    normalizeSku(
                      item?.SKU
                    ) === sku
                );

              if (
                alreadyExists
              ) {
                return previousItems;
              }

              return [
                ...previousItems,

                {
                  ...productInfo,

                  cartType:
                    "product",
                },
              ];
            }
          );

          toast.success(
            "Added to Shopping Cart"
          );

          return true;
        },
        [
          getVerifiedLicenses,
          isOwnedSku,
          isSkuInCart,
          storedCartBundles,
          fetchProductInfo,
          getCartPricing,
        ]
      );

    const addBundleToCart =
      useCallback(
        async (
          bundleSKU
        ) => {
          const sku =
            normalizeSku(
              bundleSKU
            );

          if (!sku) {
            toast.error(
              "A bundle SKU is required."
            );

            return false;
          }

          if (
            isBundleInCart(
              sku
            )
          ) {
            toast(
              "This bundle is already in your shopping cart."
            );

            return false;
          }

          let currentLicenses;

          try {
            currentLicenses =
              await getVerifiedLicenses();
          } catch (error) {
            console.error(
              "Unable to verify bundle ownership:",
              error
            );

            toast.error(
              "We could not verify your licenses. Please try again."
            );

            return false;
          }

          let bundle;
          let includedProducts;

          try {
            const bundleResponse =
              await fetchBundleInfo(
                sku
              );

            bundle =
              bundleResponse.bundle;

            includedProducts =
              await fetchBundleProducts(
                bundle,

                bundleResponse
                  .includedProducts
              );
          } catch (error) {
            console.error(
              "Unable to retrieve bundle information:",
              error
            );

            toast.error(
              error?.response
                ?.data?.error ||
                error?.message ||
                "The bundle could not be loaded."
            );

            return false;
          }

          const cartBundle = {
            ...bundle,

            cartType:
              "bundle",

            products:
              getUniqueSkus(
                bundle.products
              ),

            includedProducts,
          };

          const pricing =
            calculateBundleCartPricing(
              cartBundle,

              currentLicenses,

              getCartPricing
            );

          if (
            pricing.fullyOwned
          ) {
            toast.error(
              "You already own every product in this bundle."
            );

            return false;
          }

          /*
           * Prevent two bundles in
           * the cart from purchasing
           * the same unowned product.
           */
          const newBundleSkuSet =
            new Set(
              pricing
                .purchasableProductSkus
                .map(
                  normalizeSku
                )
            );

          const overlappingBundle =
            storedCartBundles.find(
              (
                existingBundle
              ) => {
                const existingPricing =
                  calculateBundleCartPricing(
                    existingBundle,

                    currentLicenses,

                    getCartPricing
                  );

                return existingPricing
                  .purchasableProductSkus
                  .some(
                    (
                      existingProductSku
                    ) =>
                      newBundleSkuSet.has(
                        normalizeSku(
                          existingProductSku
                        )
                      )
                  );
              }
            );

          if (
            overlappingBundle
          ) {
            toast.error(
              `${
                overlappingBundle
                  .name ||
                "Another bundle"
              } contains one or more of the same products. Remove it before adding this bundle.`
            );

            return false;
          }

          /*
           * Remove individual cart
           * products that are now
           * included in the bundle.
           */
          const includedSkuSet =
            new Set(
              pricing
                .purchasableProductSkus
                .map(
                  normalizeSku
                )
            );

          const removedIndividualCount =
            cartItems.filter(
              (item) =>
                includedSkuSet.has(
                  normalizeSku(
                    item?.SKU
                  )
                )
            ).length;

          setCartItems(
            (
              previousItems
            ) =>
              previousItems.filter(
                (item) =>
                  !includedSkuSet.has(
                    normalizeSku(
                      item?.SKU
                    )
                  )
              )
          );

          setStoredCartBundles(
            (
              previousBundles
            ) => {
              const alreadyExists =
                previousBundles.some(
                  (
                    existingBundle
                  ) =>
                    normalizeSku(
                      existingBundle
                        ?.SKU
                    ) === sku
                );

              if (
                alreadyExists
              ) {
                return previousBundles;
              }

              return [
                ...previousBundles,
                cartBundle,
              ];
            }
          );

          if (
            pricing
              .hasOwnedProducts
          ) {
            toast.success(
              `Bundle added. ${
                pricing
                  .ownedProductSkus
                  .length
              } owned product${
                pricing
                  .ownedProductSkus
                  .length === 1
                  ? " was"
                  : "s were"
              } removed from the bundle price.`
            );
          } else if (
            removedIndividualCount >
            0
          ) {
            toast.success(
              `Bundle added. ${removedIndividualCount} individual cart item${
                removedIndividualCount ===
                1
                  ? " was"
                  : "s were"
              } replaced by the bundle.`
            );
          } else {
            toast.success(
              "Bundle added to Shopping Cart"
            );
          }

          return true;
        },
        [
          isBundleInCart,
          getVerifiedLicenses,
          fetchBundleInfo,
          fetchBundleProducts,
          storedCartBundles,
          cartItems,
          getCartPricing,
        ]
      );

    const removeFromCart =
      useCallback(
        (
          productSKU
        ) => {
          const sku =
            normalizeSku(
              productSKU
            );

          setCartItems(
            (
              previousItems
            ) =>
              previousItems.filter(
                (item) =>
                  normalizeSku(
                    item?.SKU
                  ) !== sku
              )
          );
        },
        []
      );

    const removeBundleFromCart =
      useCallback(
        (
          bundleSKU
        ) => {
          const sku =
            normalizeSku(
              bundleSKU
            );

          setStoredCartBundles(
            (
              previousBundles
            ) =>
              previousBundles.filter(
                (bundle) =>
                  normalizeSku(
                    bundle?.SKU
                  ) !== sku
              )
          );
        },
        []
      );

    const clearShoppingCart =
      useCallback(() => {
        setCartItems([]);

        setStoredCartBundles(
          []
        );
      }, []);

    /*
     * These are the SKU arrays sent
     * to the checkout endpoint.
     *
     * Do not send the browser-
     * calculated prices as
     * authoritative payment data.
     */
    const checkoutProductSkus =
      useMemo(
        () =>
          getUniqueSkus(
            cartItems.map(
              (item) =>
                item?.SKU
            )
          ),
        [cartItems]
      );

    const checkoutBundleSkus =
      useMemo(
        () =>
          getUniqueSkus(
            cartBundles
              .filter(
                (bundle) =>
                  !bundle
                    .fullyOwned
              )
              .map(
                (bundle) =>
                  bundle?.SKU
              )
          ),
        [cartBundles]
      );

    const checkoutPayload =
      useMemo(
        () => ({
          productSkus:
            checkoutProductSkus,

          bundleSkus:
            checkoutBundleSkus,
        }),
        [
          checkoutProductSkus,
          checkoutBundleSkus,
        ]
      );

    return (
      <ShoppingCartContext.Provider
        value={{
          /*
           * Separate line-item
           * collections.
           */
          cartItems,
          cartBundles,

          /*
           * Totals.
           */
          productSubtotal,

          bundleBaseSubtotal,
          bundlePromotedSubtotal,
          bundleSubtotal,

          bundlePromotionDiscount,
          bundleOwnershipCredit,
          bundleTotalSavings,

          cartSubtotal,
          cartItemCount,
          cartPricingReady,

          loadingBundlePromotions:
            loadingPromotions,

          /*
           * Add and remove
           * functions.
           */
          addToCart,
          addBundleToCart,

          removeFromCart,
          removeBundleFromCart,

          clearShoppingCart,

          /*
           * Lookup helpers.
           */
          isOwnedSku,
          isSkuInCart,
          isBundleInCart,

          isSkuIncludedInCartBundle,

          /*
           * Checkout request data.
           */
          checkoutProductSkus,
          checkoutBundleSkus,
          checkoutPayload,
        }}
      >
        {children}
      </ShoppingCartContext.Provider>
    );
  };

// import {
//   createContext,
//   useCallback,
//   useContext,
//   useEffect,
//   useMemo,
//   useState,
// } from "react";

// import axios from "axios";
// import { toast } from "react-hot-toast";

// import {
//   UserContext,
// } from "../userContext/userContext";

// const PRODUCT_CART_STORAGE_KEY =
//   "shoppingCart";

// const BUNDLE_CART_STORAGE_KEY =x
//   "shoppingCartBundles";

// export const ShoppingCartContext =
//   createContext({
//     cartItems: [],
//     cartBundles: [],

//     productSubtotal: 0,
//     bundleSubtotal: 0,
//     cartSubtotal: 0,

//     bundleOwnershipCredit: 0,
//     cartItemCount: 0,
//     cartPricingReady: true,

//     addToCart: async () => false,
//     addBundleToCart: async () => false,

//     removeFromCart: () => {},
//     removeBundleFromCart: () => {},
//     clearShoppingCart: () => {},

//     isOwnedSku: () => false,
//     isSkuInCart: () => false,
//     isBundleInCart: () => false,
//     isSkuIncludedInCartBundle: () => false,

//     checkoutProductSkus: [],
//     checkoutBundleSkus: [],
//     checkoutPayload: {
//       productSkus: [],
//       bundleSkus: [],
//     },
//   });

// function normalizeSku(value) {
//   if (
//     value === null ||
//     value === undefined
//   ) {
//     return "";
//   }

//   return String(value).trim();
// }

// function roundMoney(value) {
//   return (
//     Math.round(
//       (
//         Number(value || 0) +
//         Number.EPSILON
//       ) *
//         100
//     ) / 100
//   );
// }

// function loadSavedArray(storageKey) {
//   try {
//     const savedValue =
//       localStorage.getItem(
//         storageKey
//       );

//     if (!savedValue) {
//       return [];
//     }

//     const parsedValue =
//       JSON.parse(savedValue);

//     return Array.isArray(
//       parsedValue
//     )
//       ? parsedValue
//       : [];
//   } catch (error) {
//     console.error(
//       `Unable to read ${storageKey}:`,
//       error
//     );

//     localStorage.removeItem(
//       storageKey
//     );

//     return [];
//   }
// }

// function getUniqueSkus(values) {
//   if (!Array.isArray(values)) {
//     return [];
//   }

//   return [
//     ...new Set(
//       values
//         .map(normalizeSku)
//         .filter(Boolean)
//     ),
//   ];
// }

// function getOwnedSkuSet(
//   licenses = []
// ) {
//   const ownedSkus = new Set();

//   if (!Array.isArray(licenses)) {
//     return ownedSkus;
//   }

//   for (const license of licenses) {
//     const licenseType =
//       String(
//         license?.type || ""
//       )
//         .trim()
//         .toLowerCase();

//     /*
//      * Subscription licenses do not count as ownership.
//      */
//     if (licenseType !== "own") {
//       continue;
//     }

//     const sku = normalizeSku(
//       license?.sku
//     );

//     if (sku) {
//       ownedSkus.add(sku);
//     }
//   }

//   return ownedSkus;
// }

// /*
//  * Calculates the portion of the bundle price that still needs
//  * to be paid after removing products the customer already owns.
//  *
//  * The bundle's actual price is distributed proportionally across
//  * its included products according to their individual prices.
//  *
//  * Example:
//  *
//  * Product A: $60
//  * Product B: $40
//  * Bundle price: $50
//  *
//  * Product A receives $30 of the bundle price.
//  * Product B receives $20 of the bundle price.
//  *
//  * If the customer owns Product B, the adjusted price is $30.
//  */
// function calculateBundleCartPricing(
//   bundle,
//   licenses = []
// ) {
//   const ownedSkuSet =
//     getOwnedSkuSet(licenses);

//   const bundleProductSkus =
//     getUniqueSkus(
//       bundle?.products
//     );

//   const includedProducts =
//     Array.isArray(
//       bundle?.includedProducts
//     )
//       ? bundle.includedProducts
//       : [];

//   const productMap = new Map();

//   for (
//     const product of includedProducts
//   ) {
//     const sku = normalizeSku(
//       product?.SKU
//     );

//     if (sku) {
//       productMap.set(
//         sku,
//         product
//       );
//     }
//   }

//   const ownedProductSkus = [];
//   const purchasableProductSkus =
//     [];

//   const ownedProducts = [];
//   const purchasableProducts = [];

//   let includedProductTotal = 0;
//   let ownedProductTotal = 0;
//   let purchasableProductTotal = 0;

//   for (
//     const sku of bundleProductSkus
//   ) {
//     const product =
//       productMap.get(sku);

//     const productPrice =
//       Math.max(
//         0,
//         Number(
//           product?.price || 0
//         )
//       );

//     includedProductTotal +=
//       productPrice;

//     if (ownedSkuSet.has(sku)) {
//       ownedProductSkus.push(sku);
//       ownedProductTotal +=
//         productPrice;

//       if (product) {
//         ownedProducts.push(
//           product
//         );
//       }
//     } else {
//       purchasableProductSkus.push(
//         sku
//       );

//       purchasableProductTotal +=
//         productPrice;

//       if (product) {
//         purchasableProducts.push(
//           product
//         );
//       }
//     }
//   }

//   includedProductTotal =
//     roundMoney(
//       includedProductTotal
//     );

//   ownedProductTotal =
//     roundMoney(
//       ownedProductTotal
//     );

//   purchasableProductTotal =
//     roundMoney(
//       purchasableProductTotal
//     );

//   const baseBundlePrice =
//     roundMoney(
//       Math.max(
//         0,
//         Number(
//           bundle?.price || 0
//         )
//       )
//     );

//   let adjustedPrice =
//     baseBundlePrice;

//   if (
//     purchasableProductSkus.length ===
//     0
//   ) {
//     adjustedPrice = 0;
//   } else if (
//     includedProductTotal > 0
//   ) {
//     const remainingRatio =
//       purchasableProductTotal /
//       includedProductTotal;

//     adjustedPrice = roundMoney(
//       baseBundlePrice *
//         remainingRatio
//     );
//   } else if (
//     bundleProductSkus.length > 0
//   ) {
//     /*
//      * Fallback for a bundle containing only zero-priced products.
//      */
//     const remainingRatio =
//       purchasableProductSkus.length /
//       bundleProductSkus.length;

//     adjustedPrice = roundMoney(
//       baseBundlePrice *
//         remainingRatio
//     );
//   }

//   const ownershipCredit =
//     roundMoney(
//       Math.max(
//         0,
//         baseBundlePrice -
//           adjustedPrice
//       )
//     );

//   return {
//     baseBundlePrice,
//     adjustedPrice,
//     ownershipCredit,

//     includedProductTotal,
//     ownedProductTotal,
//     purchasableProductTotal,

//     ownedProductSkus,
//     purchasableProductSkus,

//     ownedProducts,
//     purchasableProducts,

//     hasOwnedProducts:
//       ownedProductSkus.length > 0,

//     partiallyOwned:
//       ownedProductSkus.length > 0 &&
//       purchasableProductSkus.length >
//         0,

//     fullyOwned:
//       bundleProductSkus.length >
//         0 &&
//       purchasableProductSkus.length ===
//         0,
//   };
// }

// export const ShoppingCartProvider = ({
//   children,
// }) => {
//   const {
//     user,
//     userLicenses = [],
//     licensesLoaded = false,
//     refreshLicenses,
//   } = useContext(UserContext);

//   /*
//    * Individual product line items.
//    */
//   const [
//     cartItems,
//     setCartItems,
//   ] = useState(() =>
//     loadSavedArray(
//       PRODUCT_CART_STORAGE_KEY
//     )
//   );

//   /*
//    * Bundle line items remain separate from individual products.
//    *
//    * Each stored bundle also contains includedProducts so pricing
//    * and ownership adjustments can be displayed without another
//    * product request on every render.
//    */
//   const [
//     storedCartBundles,
//     setStoredCartBundles,
//   ] = useState(() =>
//     loadSavedArray(
//       BUNDLE_CART_STORAGE_KEY
//     )
//   );

//   const hasAuthentication =
//     Boolean(
//       user ||
//         localStorage.getItem(
//           "token"
//         )
//     );

//   const cartPricingReady =
//     !hasAuthentication ||
//     licensesLoaded;

//   /*
//    * Add calculated ownership and pricing details to every bundle.
//    *
//    * The stored bundle document itself remains unchanged.
//    */
//   const cartBundles = useMemo(
//     () =>
//       storedCartBundles.map(
//         (bundle) => {
//           const pricing =
//             calculateBundleCartPricing(
//               bundle,
//               licensesLoaded
//                 ? userLicenses
//                 : []
//             );

//           return {
//             ...bundle,

//             cartType:
//               "bundle",

//             basePrice:
//               pricing.baseBundlePrice,

//             adjustedPrice:
//               pricing.adjustedPrice,

//             ownershipCredit:
//               pricing.ownershipCredit,

//             includedProductTotal:
//               pricing.includedProductTotal,

//             ownedProductTotal:
//               pricing.ownedProductTotal,

//             purchasableProductTotal:
//               pricing.purchasableProductTotal,

//             ownedProductSkus:
//               pricing.ownedProductSkus,

//             purchasableProductSkus:
//               pricing.purchasableProductSkus,

//             ownedProducts:
//               pricing.ownedProducts,

//             purchasableProducts:
//               pricing.purchasableProducts,

//             hasOwnedProducts:
//               pricing.hasOwnedProducts,

//             partiallyOwned:
//               pricing.partiallyOwned,

//             fullyOwned:
//               pricing.fullyOwned,
//           };
//         }
//       ),
//     [
//       storedCartBundles,
//       licensesLoaded,
//       userLicenses,
//     ]
//   );

//   const productSubtotal =
//     useMemo(() => {
//       return roundMoney(
//         cartItems.reduce(
//           (
//             subtotal,
//             item
//           ) =>
//             subtotal +
//             Math.max(
//               0,
//               Number(
//                 item?.price || 0
//               )
//             ),
//           0
//         )
//       );
//     }, [cartItems]);

//   const bundleSubtotal =
//     useMemo(() => {
//       return roundMoney(
//         cartBundles.reduce(
//           (
//             subtotal,
//             bundle
//           ) =>
//             subtotal +
//             Math.max(
//               0,
//               Number(
//                 bundle.adjustedPrice ||
//                   0
//               )
//             ),
//           0
//         )
//       );
//     }, [cartBundles]);

//   const bundleOwnershipCredit =
//     useMemo(() => {
//       return roundMoney(
//         cartBundles.reduce(
//           (
//             total,
//             bundle
//           ) =>
//             total +
//             Math.max(
//               0,
//               Number(
//                 bundle.ownershipCredit ||
//                   0
//               )
//             ),
//           0
//         )
//       );
//     }, [cartBundles]);

//   const cartSubtotal =
//     useMemo(
//       () =>
//         roundMoney(
//           productSubtotal +
//             bundleSubtotal
//         ),
//       [
//         productSubtotal,
//         bundleSubtotal,
//       ]
//     );

//   const cartItemCount =
//     cartItems.length +
//     cartBundles.length;

//   /*
//    * Save products and bundles separately.
//    */
//   useEffect(() => {
//     localStorage.setItem(
//       PRODUCT_CART_STORAGE_KEY,
//       JSON.stringify(cartItems)
//     );
//   }, [cartItems]);

//   useEffect(() => {
//     localStorage.setItem(
//       BUNDLE_CART_STORAGE_KEY,
//       JSON.stringify(
//         storedCartBundles
//       )
//     );
//   }, [storedCartBundles]);

//   const isOwnedSku = useCallback(
//     (
//       productSKU,
//       licenses = userLicenses
//     ) => {
//       const sku =
//         normalizeSku(productSKU);

//       if (!sku) {
//         return false;
//       }

//       return getOwnedSkuSet(
//         licenses
//       ).has(sku);
//     },
//     [userLicenses]
//   );

//   const isSkuInCart = useCallback(
//     (productSKU) => {
//       const sku =
//         normalizeSku(productSKU);

//       return cartItems.some(
//         (item) =>
//           normalizeSku(
//             item?.SKU
//           ) === sku
//       );
//     },
//     [cartItems]
//   );

//   const isBundleInCart =
//     useCallback(
//       (bundleSKU) => {
//         const sku =
//           normalizeSku(bundleSKU);

//         return storedCartBundles.some(
//           (bundle) =>
//             normalizeSku(
//               bundle?.SKU
//             ) === sku
//         );
//       },
//       [storedCartBundles]
//     );

//   const isSkuIncludedInCartBundle =
//     useCallback(
//       (productSKU) => {
//         const sku =
//           normalizeSku(productSKU);

//         if (!sku) {
//           return false;
//         }

//         return cartBundles.some(
//           (bundle) =>
//             bundle.purchasableProductSkus.some(
//               (includedSku) =>
//                 normalizeSku(
//                   includedSku
//                 ) === sku
//             )
//         );
//       },
//       [cartBundles]
//     );

//   /*
//    * Retrieve the most recent license data before allowing a
//    * purchase decision.
//    */
//   const getVerifiedLicenses =
//     useCallback(async () => {
//       let currentLicenses =
//         Array.isArray(
//           userLicenses
//         )
//           ? userLicenses
//           : [];

//       const token =
//         localStorage.getItem(
//           "token"
//         );

//       if (
//         token &&
//         !licensesLoaded
//       ) {
//         if (
//           typeof refreshLicenses !==
//           "function"
//         ) {
//           throw new Error(
//             "License verification is unavailable."
//           );
//         }

//         const refreshedLicenses =
//           await refreshLicenses(
//             token
//           );

//         currentLicenses =
//           Array.isArray(
//             refreshedLicenses
//           )
//             ? refreshedLicenses
//             : [];
//       }

//       return currentLicenses;
//     }, [
//       userLicenses,
//       licensesLoaded,
//       refreshLicenses,
//     ]);

//   const fetchProductInfo =
//     useCallback(
//       async (productSKU) => {
//         const sku =
//           normalizeSku(
//             productSKU
//           );

//         const {
//           data,
//         } = await axios.post(
//           "/api/get_product",
//           {
//             productSKU: sku,
//           }
//         );

//         if (!data || data.error) {
//           throw new Error(
//             data?.error ||
//               `Product ${sku} could not be found.`
//           );
//         }

//         if (
//           normalizeSku(
//             data.SKU
//           ) !== sku
//         ) {
//           throw new Error(
//             `Product ${sku} could not be verified.`
//           );
//         }

//         return data;
//       },
//       []
//     );

//   const fetchBundleInfo =
//     useCallback(
//       async (bundleSKU) => {
//         const sku =
//           normalizeSku(
//             bundleSKU
//           );

//         const {
//           data,
//         } = await axios.post(
//           "/api/get-bundle",
//           {
//             SKU: sku,
//           }
//         );

//         if (!data || data.error) {
//           throw new Error(
//             data?.error ||
//               `Bundle ${sku} could not be found.`
//           );
//         }

//         const bundle =
//           data.bundle || data;

//         if (
//           normalizeSku(
//             bundle?.SKU
//           ) !== sku
//         ) {
//           throw new Error(
//             `Bundle ${sku} could not be verified.`
//           );
//         }

//         return bundle;
//       },
//       []
//     );

//   const fetchBundleProducts =
//     useCallback(
//       async (bundle) => {
//         const productSkus =
//           getUniqueSkus(
//             bundle?.products
//           );

//         if (
//           productSkus.length ===
//           0
//         ) {
//           throw new Error(
//             "This bundle does not contain any products."
//           );
//         }

//         const products =
//           await Promise.all(
//             productSkus.map(
//               (sku) =>
//                 fetchProductInfo(
//                   sku
//                 )
//             )
//           );

//         return products;
//       },
//       [fetchProductInfo]
//     );

//   /*
//    * Remove owned individual products after license information
//    * finishes loading.
//    *
//    * Also remove a bundle when every included product is owned.
//    */
//   useEffect(() => {
//     if (
//       !licensesLoaded ||
//       !Array.isArray(
//         userLicenses
//       )
//     ) {
//       return;
//     }

//     const ownedSkuSet =
//       getOwnedSkuSet(
//         userLicenses
//       );

//     setCartItems(
//       (previousItems) => {
//         const filteredItems =
//           previousItems.filter(
//             (item) =>
//               !ownedSkuSet.has(
//                 normalizeSku(
//                   item?.SKU
//                 )
//               )
//           );

//         return filteredItems.length ===
//           previousItems.length
//           ? previousItems
//           : filteredItems;
//       }
//     );

//     setStoredCartBundles(
//       (previousBundles) => {
//         const filteredBundles =
//           previousBundles.filter(
//             (bundle) => {
//               const pricing =
//                 calculateBundleCartPricing(
//                   bundle,
//                   userLicenses
//                 );

//               return !pricing.fullyOwned;
//             }
//           );

//         return filteredBundles.length ===
//           previousBundles.length
//           ? previousBundles
//           : filteredBundles;
//       }
//     );
//   }, [
//     licensesLoaded,
//     userLicenses,
//   ]);

//   /*
//    * An individual product should not remain in the cart when it is
//    * already being purchased through a bundle.
//    */
//   useEffect(() => {
//     if (
//       cartBundles.length === 0 ||
//       cartItems.length === 0
//     ) {
//       return;
//     }

//     const bundledSkuSet =
//       new Set(
//         cartBundles.flatMap(
//           (bundle) =>
//             bundle.purchasableProductSkus.map(
//               normalizeSku
//             )
//         )
//       );

//     setCartItems(
//       (previousItems) => {
//         const filteredItems =
//           previousItems.filter(
//             (item) =>
//               !bundledSkuSet.has(
//                 normalizeSku(
//                   item?.SKU
//                 )
//               )
//           );

//         return filteredItems.length ===
//           previousItems.length
//           ? previousItems
//           : filteredItems;
//       }
//     );
//   }, [
//     cartBundles,
//     cartItems.length,
//   ]);

//   const addToCart = useCallback(
//     async (productSKU) => {
//       const sku =
//         normalizeSku(
//           productSKU
//         );

//       if (!sku) {
//         toast.error(
//           "A product SKU is required."
//         );

//         return false;
//       }

//       let currentLicenses;

//       try {
//         currentLicenses =
//           await getVerifiedLicenses();
//       } catch (error) {
//         console.error(
//           "Unable to verify product ownership:",
//           error
//         );

//         toast.error(
//           "We could not verify your licenses. Please try again."
//         );

//         return false;
//       }

//       if (
//         isOwnedSku(
//           sku,
//           currentLicenses
//         )
//       ) {
//         toast.error(
//           "You have already purchased this product."
//         );

//         setCartItems(
//           (previousItems) =>
//             previousItems.filter(
//               (item) =>
//                 normalizeSku(
//                   item?.SKU
//                 ) !== sku
//             )
//         );

//         return false;
//       }

//       /*
//        * Prevent buying the same product individually and through a
//        * bundle in the same order.
//        */
//       const includedInBundle =
//         storedCartBundles.some(
//           (bundle) => {
//             const pricing =
//               calculateBundleCartPricing(
//                 bundle,
//                 currentLicenses
//               );

//             return pricing.purchasableProductSkus.some(
//               (includedSku) =>
//                 normalizeSku(
//                   includedSku
//                 ) === sku
//             );
//           }
//         );

//       if (includedInBundle) {
//         toast(
//           "This product is already included in a bundle in your cart."
//         );

//         return false;
//       }

//       if (isSkuInCart(sku)) {
//         toast(
//           "This product is already in your shopping cart."
//         );

//         return false;
//       }

//       let productInfo;

//       try {
//         productInfo =
//           await fetchProductInfo(
//             sku
//           );
//       } catch (error) {
//         console.error(
//           "Unable to retrieve product information:",
//           error
//         );

//         toast.error(
//           error?.response?.data
//             ?.error ||
//             error?.message ||
//             "Failed to retrieve product details."
//         );

//         return false;
//       }

//       setCartItems(
//         (previousItems) => {
//           const alreadyExists =
//             previousItems.some(
//               (item) =>
//                 normalizeSku(
//                   item?.SKU
//                 ) === sku
//             );

//           if (alreadyExists) {
//             return previousItems;
//           }

//           return [
//             ...previousItems,
//             {
//               ...productInfo,
//               cartType:
//                 "product",
//             },
//           ];
//         }
//       );

//       toast.success(
//         "Added to Shopping Cart"
//       );

//       return true;
//     },
//     [
//       getVerifiedLicenses,
//       isOwnedSku,
//       isSkuInCart,
//       storedCartBundles,
//       fetchProductInfo,
//     ]
//   );

//   const addBundleToCart =
//     useCallback(
//       async (bundleSKU) => {
//         const sku =
//           normalizeSku(
//             bundleSKU
//           );

//         if (!sku) {
//           toast.error(
//             "A bundle SKU is required."
//           );

//           return false;
//         }

//         if (
//           isBundleInCart(sku)
//         ) {
//           toast(
//             "This bundle is already in your shopping cart."
//           );

//           return false;
//         }

//         let currentLicenses;

//         try {
//           currentLicenses =
//             await getVerifiedLicenses();
//         } catch (error) {
//           console.error(
//             "Unable to verify bundle ownership:",
//             error
//           );

//           toast.error(
//             "We could not verify your licenses. Please try again."
//           );

//           return false;
//         }

//         let bundle;
//         let includedProducts;

//         try {
//           bundle =
//             await fetchBundleInfo(
//               sku
//             );

//           includedProducts =
//             await fetchBundleProducts(
//               bundle
//             );
//         } catch (error) {
//           console.error(
//             "Unable to retrieve bundle information:",
//             error
//           );

//           toast.error(
//             error?.response?.data
//               ?.error ||
//               error?.message ||
//               "The bundle could not be loaded."
//           );

//           return false;
//         }

//         const cartBundle = {
//           ...bundle,

//           cartType:
//             "bundle",

//           products:
//             getUniqueSkus(
//               bundle.products
//             ),

//           includedProducts,
//         };

//         const pricing =
//           calculateBundleCartPricing(
//             cartBundle,
//             currentLicenses
//           );

//         if (pricing.fullyOwned) {
//           toast.error(
//             "You already own every product in this bundle."
//           );

//           return false;
//         }

//         /*
//          * Prevent two bundles in the cart from purchasing the same
//          * unowned product.
//          */
//         const newBundleSkuSet =
//           new Set(
//             pricing.purchasableProductSkus.map(
//               normalizeSku
//             )
//           );

//         const overlappingBundle =
//           storedCartBundles.find(
//             (existingBundle) => {
//               const existingPricing =
//                 calculateBundleCartPricing(
//                   existingBundle,
//                   currentLicenses
//                 );

//               return existingPricing.purchasableProductSkus.some(
//                 (
//                   existingProductSku
//                 ) =>
//                   newBundleSkuSet.has(
//                     normalizeSku(
//                       existingProductSku
//                     )
//                   )
//               );
//             }
//           );

//         if (overlappingBundle) {
//           toast.error(
//             `${overlappingBundle.name || "Another bundle"} contains one or more of the same products. Remove it before adding this bundle.`
//           );

//           return false;
//         }

//         /*
//          * Remove individual cart products that are now included in
//          * the bundle.
//          */
//         const includedSkuSet =
//           new Set(
//             pricing.purchasableProductSkus.map(
//               normalizeSku
//             )
//           );

//         const removedIndividualCount =
//           cartItems.filter(
//             (item) =>
//               includedSkuSet.has(
//                 normalizeSku(
//                   item?.SKU
//                 )
//               )
//           ).length;

//         setCartItems(
//           (previousItems) =>
//             previousItems.filter(
//               (item) =>
//                 !includedSkuSet.has(
//                   normalizeSku(
//                     item?.SKU
//                   )
//                 )
//             )
//         );

//         setStoredCartBundles(
//           (previousBundles) => {
//             const alreadyExists =
//               previousBundles.some(
//                 (existingBundle) =>
//                   normalizeSku(
//                     existingBundle?.SKU
//                   ) === sku
//               );

//             if (alreadyExists) {
//               return previousBundles;
//             }

//             return [
//               ...previousBundles,
//               cartBundle,
//             ];
//           }
//         );

//         if (
//           pricing.hasOwnedProducts
//         ) {
//           toast.success(
//             `Bundle added. ${pricing.ownedProductSkus.length} owned product${
//               pricing.ownedProductSkus.length ===
//               1
//                 ? " was"
//                 : "s were"
//             } removed from the bundle price.`
//           );
//         } else if (
//           removedIndividualCount > 0
//         ) {
//           toast.success(
//             `Bundle added. ${removedIndividualCount} individual cart item${
//               removedIndividualCount ===
//               1
//                 ? " was"
//                 : "s were"
//             } replaced by the bundle.`
//           );
//         } else {
//           toast.success(
//             "Bundle added to Shopping Cart"
//           );
//         }

//         return true;
//       },
//       [
//         isBundleInCart,
//         getVerifiedLicenses,
//         fetchBundleInfo,
//         fetchBundleProducts,
//         storedCartBundles,
//         cartItems,
//       ]
//     );

//   const removeFromCart =
//     useCallback(
//       (productSKU) => {
//         const sku =
//           normalizeSku(
//             productSKU
//           );

//         setCartItems(
//           (previousItems) =>
//             previousItems.filter(
//               (item) =>
//                 normalizeSku(
//                   item?.SKU
//                 ) !== sku
//             )
//         );
//       },
//       []
//     );

//   const removeBundleFromCart =
//     useCallback(
//       (bundleSKU) => {
//         const sku =
//           normalizeSku(
//             bundleSKU
//           );

//         setStoredCartBundles(
//           (previousBundles) =>
//             previousBundles.filter(
//               (bundle) =>
//                 normalizeSku(
//                   bundle?.SKU
//                 ) !== sku
//             )
//         );
//       },
//       []
//     );

//   const clearShoppingCart =
//     useCallback(() => {
//       setCartItems([]);
//       setStoredCartBundles(
//         []
//       );
//     }, []);

//   /*
//    * These are the arrays that should eventually be sent to the
//    * checkout endpoint.
//    */
//   const checkoutProductSkus =
//     useMemo(
//       () =>
//         getUniqueSkus(
//           cartItems.map(
//             (item) => item?.SKU
//           )
//         ),
//       [cartItems]
//     );

//   const checkoutBundleSkus =
//     useMemo(
//       () =>
//         getUniqueSkus(
//           cartBundles
//             .filter(
//               (bundle) =>
//                 !bundle.fullyOwned
//             )
//             .map(
//               (bundle) =>
//                 bundle?.SKU
//             )
//         ),
//       [cartBundles]
//     );

//   const checkoutPayload =
//     useMemo(
//       () => ({
//         productSkus:
//           checkoutProductSkus,

//         bundleSkus:
//           checkoutBundleSkus,
//       }),
//       [
//         checkoutProductSkus,
//         checkoutBundleSkus,
//       ]
//     );

//   return (
//     <ShoppingCartContext.Provider
//       value={{
//         /*
//          * Separate line-item collections.
//          */
//         cartItems,
//         cartBundles,

//         /*
//          * Totals.
//          */
//         productSubtotal,
//         bundleSubtotal,
//         cartSubtotal,
//         bundleOwnershipCredit,
//         cartItemCount,
//         cartPricingReady,

//         /*
//          * Add and remove functions.
//          */
//         addToCart,
//         addBundleToCart,
//         removeFromCart,
//         removeBundleFromCart,
//         clearShoppingCart,

//         /*
//          * Lookup helpers.
//          */
//         isOwnedSku,
//         isSkuInCart,
//         isBundleInCart,
//         isSkuIncludedInCartBundle,

//         /*
//          * Future checkout request data.
//          */
//         checkoutProductSkus,
//         checkoutBundleSkus,
//         checkoutPayload,
//       }}
//     >
//       {children}
//     </ShoppingCartContext.Provider>
//   );
// };

// // import {
// //   createContext,
// //   useCallback,
// //   useContext,
// //   useEffect,
// //   useMemo,
// //   useState,
// // } from "react";

// // import axios from "axios";
// // import { toast } from "react-hot-toast";

// // import {
// //   UserContext,
// // } from "../userContext/userContext";

// // export const ShoppingCartContext = createContext({
// //   cartItems: [],
// //   cartSubtotal: 0,
// //   addToCart: async () => false,
// //   removeFromCart: () => {},
// //   clearShoppingCart: () => {},
// //   isOwnedSku: () => false,
// //   isSkuInCart: () => false,
// // });

// // function loadSavedCart() {
// //   try {
// //     const savedCart =
// //       localStorage.getItem("shoppingCart");

// //     if (!savedCart) {
// //       return [];
// //     }

// //     const parsedCart =
// //       JSON.parse(savedCart);

// //     return Array.isArray(parsedCart)
// //       ? parsedCart
// //       : [];
// //   } catch (error) {
// //     console.error(
// //       "Unable to read the saved shopping cart:",
// //       error
// //     );

// //     localStorage.removeItem(
// //       "shoppingCart"
// //     );

// //     return [];
// //   }
// // }

// // export const ShoppingCartProvider = ({
// //   children,
// // }) => {
// //   const {
// //     user,
// //     userLicenses = [],
// //     licensesLoaded = false,
// //     refreshLicenses,
// //   } = useContext(UserContext);

// //   const [
// //     cartItems,
// //     setCartItems,
// //   ] = useState(loadSavedCart);

// //   /*
// //    * Calculate the subtotal directly from the cart instead of
// //    * storing another value that must be manually synchronized.
// //    */
// //   const cartSubtotal = useMemo(() => {
// //     return cartItems.reduce(
// //       (subtotal, item) => {
// //         const price = Number(
// //           item?.price || 0
// //         );

// //         return subtotal + price;
// //       },
// //       0
// //     );
// //   }, [cartItems]);

// //   /*
// //    * Save the cart whenever its contents change.
// //    */
// //   useEffect(() => {
// //     localStorage.setItem(
// //       "shoppingCart",
// //       JSON.stringify(cartItems)
// //     );
// //   }, [cartItems]);

// //   /*
// //    * Only permanent ownership blocks a purchase.
// //    *
// //    * A "subscribe" license intentionally returns false here.
// //    */
// //   const isOwnedSku = useCallback(
// //     (
// //       productSKU,
// //       licenses = userLicenses
// //     ) => {
// //       if (
// //         !productSKU ||
// //         !Array.isArray(licenses)
// //       ) {
// //         return false;
// //       }

// //       return licenses.some(
// //         (license) => {
// //           const sameSku =
// //             String(license?.sku) ===
// //             String(productSKU);

// //           const isOwned =
// //             String(
// //               license?.type || ""
// //             )
// //               .trim()
// //               .toLowerCase() ===
// //             "own";

// //           return sameSku && isOwned;
// //         }
// //       );
// //     },
// //     [userLicenses]
// //   );

// //   const isSkuInCart = useCallback(
// //     (productSKU) => {
// //       return cartItems.some(
// //         (item) =>
// //           String(item?.SKU) ===
// //           String(productSKU)
// //       );
// //     },
// //     [cartItems]
// //   );

// //   /*
// //    * If the user signs in with an owned product already saved in
// //    * the browser cart, remove that product once licenses load.
// //    */
// //   useEffect(() => {
// //     if (
// //       !user ||
// //       !licensesLoaded ||
// //       !Array.isArray(userLicenses)
// //     ) {
// //       return;
// //     }

// //     setCartItems((previousItems) => {
// //       const filteredItems =
// //         previousItems.filter(
// //           (item) =>
// //             !isOwnedSku(
// //               item?.SKU,
// //               userLicenses
// //             )
// //         );

// //       return filteredItems.length ===
// //         previousItems.length
// //         ? previousItems
// //         : filteredItems;
// //     });
// //   }, [
// //     user,
// //     userLicenses,
// //     licensesLoaded,
// //     isOwnedSku,
// //   ]);

// //   const getProductInfo = useCallback(
// //     async (productSKU) => {
// //       try {
// //         const { data } =
// //           await axios.post(
// //             "/api/get_product",
// //             {
// //               productSKU,
// //             }
// //           );

// //         if (!data || data.error) {
// //           toast.error(
// //             data?.error ||
// //               "Product not found."
// //           );

// //           return null;
// //         }

// //         return data;
// //       } catch (error) {
// //         console.error(
// //           "Unable to retrieve product information:",
// //           error
// //         );

// //         toast.error(
// //           error?.response?.data?.error ||
// //             "Failed to retrieve product details."
// //         );

// //         return null;
// //       }
// //     },
// //     []
// //   );

// //   const addToCart = useCallback(
// //     async (productSKU) => {
// //       if (
// //         productSKU === null ||
// //         productSKU === undefined ||
// //         productSKU === ""
// //       ) {
// //         toast.error(
// //           "A product SKU is required."
// //         );

// //         return false;
// //       }

// //       let currentLicenses =
// //         Array.isArray(userLicenses)
// //           ? userLicenses
// //           : [];

// //       /*
// //        * A signed-in user may click before the license request has
// //        * completed. Refresh the licenses before deciding whether the
// //        * purchase is allowed.
// //        */
// //       const token =
// //         localStorage.getItem("token");

// //       if (
// //         token &&
// //         !licensesLoaded &&
// //         typeof refreshLicenses ===
// //           "function"
// //       ) {
// //         try {
// //           const refreshedLicenses =
// //             await refreshLicenses(token);

// //           if (
// //             Array.isArray(
// //               refreshedLicenses
// //             )
// //           ) {
// //             currentLicenses =
// //               refreshedLicenses;
// //           }
// //         } catch (error) {
// //           console.error(
// //             "Unable to verify product ownership:",
// //             error
// //           );

// //           toast.error(
// //             "We could not verify your licenses. Please try again."
// //           );

// //           return false;
// //         }
// //       }

// //       if (
// //         isOwnedSku(
// //           productSKU,
// //           currentLicenses
// //         )
// //       ) {
// //         toast.error(
// //           "You have already purchased this product."
// //         );

// //         /*
// //          * Also remove a stale copy if it was already stored in the
// //          * browser cart.
// //          */
// //         setCartItems(
// //           (previousItems) =>
// //             previousItems.filter(
// //               (item) =>
// //                 String(item?.SKU) !==
// //                 String(productSKU)
// //             )
// //         );

// //         return false;
// //       }

// //       /*
// //        * Check before loading the product document so clicking the
// //        * button repeatedly does not produce unnecessary API requests.
// //        */
// //       if (isSkuInCart(productSKU)) {
// //         toast(
// //           "This product is already in your shopping cart."
// //         );

// //         return false;
// //       }

// //       const productInfo =
// //         await getProductInfo(
// //           productSKU
// //         );

// //       if (!productInfo) {
// //         return false;
// //       }

// //       /*
// //        * Verify the returned product has the expected SKU.
// //        */
// //       if (
// //         String(productInfo.SKU) !==
// //         String(productSKU)
// //       ) {
// //         toast.error(
// //           "The requested product could not be verified."
// //         );

// //         return false;
// //       }

// //       let itemWasAdded = false;

// //       setCartItems(
// //         (previousItems) => {
// //           const alreadyExists =
// //             previousItems.some(
// //               (item) =>
// //                 String(item?.SKU) ===
// //                 String(
// //                   productInfo.SKU
// //                 )
// //             );

// //           if (alreadyExists) {
// //             return previousItems;
// //           }

// //           itemWasAdded = true;

// //           return [
// //             ...previousItems,
// //             productInfo,
// //           ];
// //         }
// //       );

// //       /*
// //        * React state updates are queued, but because we already checked
// //        * isSkuInCart above, this will normally be true.
// //        */
// //       if (itemWasAdded) {
// //         toast.success(
// //           "Added to Shopping Cart"
// //         );

// //         return true;
// //       }

// //       toast(
// //         "This product is already in your shopping cart."
// //       );

// //       return false;
// //     },
// //     [
// //       userLicenses,
// //       licensesLoaded,
// //       refreshLicenses,
// //       isOwnedSku,
// //       isSkuInCart,
// //       getProductInfo,
// //     ]
// //   );

// //   const removeFromCart =
// //     useCallback((productSKU) => {
// //       setCartItems(
// //         (previousItems) =>
// //           previousItems.filter(
// //             (item) =>
// //               String(item?.SKU) !==
// //               String(productSKU)
// //           )
// //       );
// //     }, []);

// //   const clearShoppingCart =
// //     useCallback(() => {
// //       setCartItems([]);
// //     }, []);

// //   return (
// //     <ShoppingCartContext.Provider
// //       value={{
// //         cartItems,
// //         cartSubtotal,
// //         addToCart,
// //         removeFromCart,
// //         clearShoppingCart,
// //         isOwnedSku,
// //         isSkuInCart,
// //       }}
// //     >
// //       {children}
// //     </ShoppingCartContext.Provider>
// //   );
// // };

// // // import { createContext, useState, useEffect } from 'react';
// // // import axios from 'axios';
// // // import { toast } from 'react-hot-toast';

// // // // Create the shopping cart context
// // // export const ShoppingCartContext = createContext();

// // // export const ShoppingCartProvider = ({ children }) => {
  
// // //   const [cartItems, setCartItems] = useState(() => {
// // //     // Retrieve the cart from localStorage if it exists
// // //     const savedCart = localStorage.getItem('shoppingCart');
// // //     return savedCart ? JSON.parse(savedCart) : [];
// // //   });

// // //   const [cartSubtotal, setCartSubtotal] = useState(0)

// // //   // Save cart items to localStorage whenever it changes
// // //   useEffect(() => {

// // //     //console.log("CART ITEMS:", cartItems)
// // //     //console.log("ITERATOR:")
// // //     let subtotal = 0;
// // //     for(var i =0; i < cartItems.length; i++)
// // //     {
// // //         subtotal = subtotal + cartItems[i].price
// // //     }
// // //     setCartSubtotal(subtotal)

// // //     localStorage.setItem('shoppingCart', JSON.stringify(cartItems))
// // //     //console.log(cartSubtotal);
// // //   }, [cartItems]);



// // //   const addToCart = async (productSKU) => {

// // //     console.log("incoming SKU: ", productSKU)
// // //     const productInfo = await getProductInfo(productSKU);
    
// // //     console.log(productSKU)

// // //     if(productInfo)
// // //     {
// // //       toast.success("Added to Shopping Cart")
// // //     }
// // //     if (!productInfo) {
// // //       toast.error("Product not found!");
// // //       return;
// // //     }

    

// // //     setCartItems((prevItems) => {
// // //       // Check if the product is already in the cart
// // //       const itemExists = prevItems.some(item => item.SKU === productInfo.SKU);
      
// // //       // Only add the product if it doesn't already exist
// // //       if (!itemExists) {
// // //         return [...prevItems, productInfo];
// // //       }

// // //       return prevItems; // If it exists, return the existing array unchanged
// // //     });
// // //   };

// // //   const clearShoppingCart = () => {
// // //     setCartItems([])
// // //     localStorage.setItem('shoppingCart', JSON.stringify(cartItems))
// // //   }

// // //   const removeFromCart = (productId) => {    
// // //     setCartItems((prevItems) => prevItems.filter(item => item.SKU !== productId));
// // //   };

// // //   // Fetch product details by SKU
// // //   const getProductInfo = async (productSKU) => {
// // //     console.log("Searching for Product: ", productSKU);

// // //     try {
// // //       const { data } = await axios.post('/api/get_product', { productSKU });

// // //       if (data.error) {
// // //         toast.error(data.error);
// // //         return null;
// // //       }

// // //       console.log("Product Data: ", data);
// // //       return data; // Full product details returned from backend
// // //     } catch (error) {
// // //       console.log(error);
// // //       toast.error("Failed to fetch product details");
// // //       return null;
// // //     }
// // //   };

// // //   return (
// // //     <ShoppingCartContext.Provider value={{ cartItems, cartSubtotal, addToCart, removeFromCart, clearShoppingCart }}>
// // //       {children}
// // //     </ShoppingCartContext.Provider>
// // //   );
// // // };


